import { Software } from '@/types/software';
import { DialogContent, DialogTitle } from '@/components/ui/dialog';
import { differenceInYears } from 'date-fns';

interface SoftwareDetailsProps {
  software: Software;
}

export const SoftwareDetails = ({ software }: SoftwareDetailsProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const calculateYearsOfService = (launchDate: string, endDate: string) => {
    try {
      const years = differenceInYears(new Date(endDate), new Date(launchDate));
      return years;
    } catch {
      return 0;
    }
  };

  const query = encodeURIComponent(software.name);

  return (
    <DialogContent className="bg-gradient-card border-memorial-border text-memorial-text max-w-2xl">
      <div className="space-y-6 py-4">
        <div className="space-y-3 text-center">
          <div className="mb-3">
            {software.image ? (
              <img
                src={software.image}
                alt={software.name}
                className="mx-auto h-24 w-24 object-contain"
              />
            ) : (
              <div className="text-6xl">{software.logo}</div>
            )}
          </div>
          <DialogTitle className="text-3xl font-bold">{software.name}</DialogTitle>
        </div>

        {software.description && (
          <p className="text-memorial-muted text-lg leading-relaxed">{software.description}</p>
        )}

        <div className="border-memorial-border space-y-4 border-y py-4">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1">
              <span className="text-memorial-muted text-sm">Launched</span>
              <div className="text-cyber-glow font-mono text-lg">
                {formatDate(software.launchDate)}
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-memorial-muted text-sm">Obsolete</span>
              <div className="font-mono text-lg text-red-400">{formatDate(software.endDate)}</div>
            </div>
          </div>
          <div className="bg-cyber-glow/10 border-cyber-glow/30 rounded-lg border p-3 text-center">
            <span className="text-memorial-muted text-sm">Years of Service: </span>
            <span className="text-cyber-glow text-lg font-semibold">
              {calculateYearsOfService(software.launchDate, software.endDate)} years
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {software.url ? (
            <div className="space-y-2">
              <span className="text-memorial-muted text-sm font-medium">Website</span>
              <div className="bg-memorial-bg/50 border-memorial-border rounded-lg border p-3">
                <a
                  href={software.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-glow break-all hover:underline"
                >
                  {software.url}
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <span className="text-memorial-muted text-sm font-medium">Suggested Links</span>
              <div className="bg-memorial-bg/50 border-memorial-border space-y-2 rounded-lg border p-3">
                <a
                  href={`https://en.wikipedia.org/wiki/${query}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-glow block hover:underline"
                >
                  📖 Wikipedia
                </a>
                <a
                  href={`https://duckduckgo.com/?q=${query}+software`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-glow block hover:underline"
                >
                  🔍 DuckDuckGo Search
                </a>
                <a
                  href={`https://web.archive.org/web/*/${query}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-glow block hover:underline"
                >
                  🕰️ Wayback Machine
                </a>
              </div>
            </div>
          )}

          {software.repoUrl ? (
            <div className="space-y-2">
              <span className="text-memorial-muted text-sm font-medium">Repository</span>
              <div className="bg-memorial-bg/50 border-memorial-border rounded-lg border p-3">
                <a
                  href={software.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-glow break-all hover:underline"
                >
                  {software.repoUrl}
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <span className="text-memorial-muted text-sm font-medium">GitHub</span>
              <div className="bg-memorial-bg/50 border-memorial-border rounded-lg border p-3">
                <a
                  href={`https://github.com/search?q=${query}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyber-glow hover:underline"
                >
                  🔎 Search on GitHub
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </DialogContent>
  );
};
